/**
 * Review modal submit handler: persists review data and order review status.
 * @module handleSubmit
 */

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { saveReviewData } from "../../../../api/postCall/saveReviewData";
import { updateOrderReviewStatus } from "../../../../api/postCall/updateOrderReviewStatus";
import { ToastMessages } from "../../../../components/toasts/toastMessages";
import { MedicalOrderReviewStatus } from "../../../../enums/orderReviewStatus";
import { showApiError } from "../../../../utils/showApiError";

/** Arguments passed into the review modal submit handler. */
interface HandleSubmitParams {
  orderId: string;
  orgId: string;
  patientId: string;
  reviewedBy: string;
  selectedAction: "approve" | "reject";
  comments: string;
  uploadedFileUrls: string[];
  onSubmit?: () => void;
  onClose: () => void;
}

/**
 * Saves reviewer decision and updates `medical_order_review_status`, then closes the modal.
 *
 * @param params - Review form values and lifecycle callbacks.
 * @returns Resolves when persistence succeeds; no-ops when action is missing or reject has empty comments.
 * @throws Re-throws after surfacing an error toast if save or status update fails.
 */
export const handleSubmit = async (
  params: HandleSubmitParams,
): Promise<void> => {
  const {
    orderId,
    orgId,
    patientId,
    reviewedBy,
    selectedAction,
    comments,
    uploadedFileUrls,
    onSubmit,
    onClose,
  } = params;

  if (!selectedAction || (selectedAction === "reject" && !comments.trim())) {
    return;
  }

  try {
    const filePathToSave =
      uploadedFileUrls.length > 0 ? uploadedFileUrls[0] : "";
    await saveReviewData({
      orderId,
      orgId,
      data: {
        patient_id: patientId,
        status: selectedAction,
        comments: comments.trim(),
        file_path: filePathToSave,
        reviewed_by: reviewedBy,
      },
    });
    await updateOrderReviewStatus({
      order_id: orderId,
      medical_order_review_status:
        selectedAction === "approve"
          ? MedicalOrderReviewStatus.AGENT_ORDER_PASSED_BY_HUMAN
          : MedicalOrderReviewStatus.AGENT_ORDER_REJECTED_BY_HUMAN,
    });

    if (onSubmit) {
      onSubmit();
    }
    toast.success(ToastMessages.REVIEW_SUBMITTED_SUCCESSFULLY);
    onClose();
  } catch (error) {
    showApiError(error, ToastMessages.ERROR_UPDATING_ORDER_REVIEW_STATUS);
    console.error("Error saving review:", error);
    throw error;
  }
};
